/**
 * GlobalImportModal — AI/PDF & CSV Document Import Wizard
 *
 * Supports:
 *   Opening Balances, GL Accounts, Chart of Accounts,
 *   Bank Reconciliation (BRS), Purchase Invoices, Journal Entries, Generic
 *
 * Two import paths per doc type:
 *   • AI/PDF  — Upload PDF → Sarvam Vision OCR → map GL accounts → save
 *   • CSV     — Paste/upload CSV rows → preview → save directly
 *
 * Props:
 *   isOpen          boolean
 *   onClose         () => void
 *   theme           'dark' | 'light'
 *   preSelectedType string | null  — if set, skips the type-selector step
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Upload, FileText, Loader2, CheckCircle2, Save, AlertCircle, Search, Table2, Download } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API = API_BASE;


// ── Document type registry ────────────────────────────────────────────────────
// NOTE: this wizard is only reachable today via Document Hub's Financial module
// (DocumentHubModal passes preSelectedType, skipping Step 0) for the 3 master-data
// CSV/AI imports below — these write directly to GL master tables, so they don't
// go through the Doc Hub staging/review pipeline the way real vouchers do.
const DOC_TYPES = [
  { id: 'opening_balance',   label: 'Opening Balances',      color: '#6366f1', csv: true,  ai: true,
    csvCols: ['GLId','GLName','DrOpenBal','CrOpenBal'], csvEndpoint: '/api/import/opening-balance/csv',
    csvSample: [['160-AA0001','Cash Account','50000','0'],['160-AA0002','Bank Account','0','10000']],
    desc: 'Opening entries for all GL accounts (Dr/Cr balances)' },
  { id: 'gl_accounts',       label: 'GL / General Ledger',   color: '#0ea5e9', csv: true,  ai: false,
    csvCols: ['GLId','GLName','GlType','GroupId','DrOpenBal','CrOpenBal'], csvEndpoint: '/api/import/gl-accounts/csv',
    csvSample: [['160-AA0001','Cash Account','Assets','GRP001','50000','0'],['160-AA0002','Bank Account','Assets','GRP001','0','10000']],
    desc: 'Bulk create or update GL Account master records' },
  { id: 'chart_of_accounts', label: 'Chart of Accounts',     color: '#10b981', csv: true,  ai: false,
    csvCols: ['GroupId','GroupName','PrimaryGroup','ParentGroup','GLevel'], csvEndpoint: '/api/import/chart-accounts/csv',
    csvSample: [['GRP001','Current Assets','Assets','Null','2'],['GRP002','Fixed Assets','Assets','Null','2']],
    desc: 'Import account groups / CoA tree structure' },
];

const STEP_LABELS = ['Document Type', 'Choose Method', 'Upload', 'Review & Map', 'Save'];
const PARSE_STAGES = [
  'Creating Sarvam digitization job…',
  'Uploading PDF for OCR…',
  'Extracting tables from document…',
  'Matching accounts against ERP General Ledger…',
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function parseCSVText(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    return headers.reduce((obj, h, i) => { obj[h] = vals[i] || ''; return obj; }, {});
  });
}

export default function GlobalImportModal({ isOpen, onClose, theme = 'dark', preSelectedType = null }) {
  const isDark = theme === 'dark';

  // step: 0=type, 1=method, 2=upload, 3=review, 4=done
  const [step, setStep]               = useState(0);
  const [docType, setDocType]         = useState(null);
  const [method, setMethod]           = useState(null);     // 'ai' | 'csv'
  const [file, setFile]               = useState(null);
  const [csvText, setCsvText]         = useState('');
  const [csvRows, setCsvRows]         = useState([]);
  const [isDragging, setIsDragging]   = useState(false);
  const [parsing, setParsing]         = useState(false);
  const [parseStage, setParseStage]   = useState(0);
  const [parseError, setParseError]   = useState('');
  const [rows, setRows]               = useState([]);
  const [matchStats, setMatchStats]   = useState(null);
  const [accounts, setAccounts]       = useState([]);
  const [acctLoading, setAcctLoading] = useState(false);
  const [saving, setSaving]           = useState(false);
  const [saveResult, setSaveResult]   = useState(null);
  const [saveError, setSaveError]     = useState('');
  const [narration, setNarration]     = useState('');
  const fileRef = useRef(null);
  const csvRef  = useRef(null);

  const dtMeta = DOC_TYPES.find(d => d.id === docType);

  // ── Reset / pre-select on open ─────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setStep(preSelectedType ? 1 : 0);
      setDocType(preSelectedType || null);
      setMethod(null); setFile(null); setCsvText(''); setCsvRows([]);
      setParseError(''); setRows([]); setSaveResult(null); setSaveError('');
      setNarration(''); setParsing(false); setAccounts([]); setMatchStats(null); setParseStage(0);
    }
  }, [isOpen, preSelectedType]);

  // ── Simulated step-by-step progress while Sarvam OCR runs (real pipeline
  // is a single ~40-90s request; this gives the user visible progress) ──────
  useEffect(() => {
    if (!parsing) { setParseStage(0); return; }
    const timers = [
      setTimeout(() => setParseStage(1), 2500),
      setTimeout(() => setParseStage(2), 12000),
      setTimeout(() => setParseStage(3), 30000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [parsing]);

  // ── Load GL accounts for mapping ───────────────────────────────────────────
  const loadAccounts = useCallback(async (q = '') => {
    setAcctLoading(true);
    try {
      const url = `${API}/api/import/accounts?limit=400${q ? '&search=' + encodeURIComponent(q) : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      const data = await res.json();
      if (data.success) setAccounts(data.data || []);
    } catch (_) { /* ignore */ }
    setAcctLoading(false);
  }, []);

  useEffect(() => {
    if (step === 3 && method === 'ai') loadAccounts('');
  }, [step, method, loadAccounts]);

  // ── File drop zone ─────────────────────────────────────────────────────────
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  }, []);

  // ── AI parse PDF ───────────────────────────────────────────────────────────
  const handleParse = useCallback(async () => {
    if (!file) return;
    setParsing(true); setStep(2); setParseError('');
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('docType', docType);
      const res = await fetch(`${API}/api/import/parse`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: form,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Parsing failed');
      if (docType === 'opening_balance') {
        // Rows already auto-matched against M_GeneralLedger by GLId on the backend
        const mapped = (data.extractedRows || []).map(r => ({
          accountId: r.accountId, description: r.accountName,
          amount: r.debit > 0 ? r.debit : r.credit,
          type: r.debit > 0 ? 'DR' : 'CR',
          glId: r.glId || '', glName: r.glName || '', matched: !!r.matched,
        })).sort((a, b) => Number(a.matched) - Number(b.matched)); // unmatched first
        setRows(mapped);
        setMatchStats({ matched: data.matchedCount, unmatched: data.unmatchedCount, total: data.totalRows });
      } else {
        setRows((data.extractedRows || []).map(r => ({ ...r, glId: '', glName: '' })));
        setMatchStats(null);
      }
      setNarration(`AI Import: ${docType} — ${file.name}`);
      setStep(3);
    } catch (err) { setParseError(err.message); }
    setParsing(false);
  }, [file, docType]);

  // ── Parse CSV text ─────────────────────────────────────────────────────────
  const handleCsvParse = useCallback(() => {
    const parsed = parseCSVText(csvText);
    setCsvRows(parsed);
    setStep(3);
  }, [csvText]);

  // ── Save AI-mapped rows ────────────────────────────────────────────────────
  const handleSaveAI = useCallback(async () => {
    const mapped = rows.filter(r => r.glId);
    if (!mapped.length) { setSaveError('Assign at least one GL account before saving.'); return; }
    setSaving(true); setSaveError('');
    try {
      const entries = mapped.map(r => ({
        glId: r.glId, glName: r.glName, description: r.description,
        drAmount: r.type === 'DR' ? r.amount : 0,
        crAmount: r.type === 'CR' ? r.amount : 0,
      }));
      const res = await fetch(`${API}/api/import/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ docType, entries, narration }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      setSaveResult({ saved: data.saved }); setStep(4);
    } catch (err) { setSaveError(err.message); }
    setSaving(false);
  }, [rows, docType, narration]);

  // ── Download CSV template ─────────────────────────────────────────────────
  const handleDownloadTemplate = useCallback(() => {
    if (!dtMeta?.csvCols?.length) return;
    const header = dtMeta.csvCols.join(',');
    const sampleRows = (dtMeta.csvSample || []).map(row => row.join(','));
    const csvContent = [header, ...sampleRows].join('\r\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${dtMeta.id}_template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [dtMeta]);

  // ── Save CSV rows ──────────────────────────────────────────────────────────
  const handleSaveCSV = useCallback(async () => {
    if (!dtMeta?.csvEndpoint || !csvRows.length) { setSaveError('No rows to save.'); return; }
    setSaving(true); setSaveError('');
    try {
      const res = await fetch(`${API}${dtMeta.csvEndpoint}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rows: csvRows }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Save failed');
      setSaveResult({ inserted: data.inserted, updated: data.updated, errors: data.errors });
      setStep(4);
    } catch (err) { setSaveError(err.message); }
    setSaving(false);
  }, [csvRows, dtMeta]);

  if (!isOpen) return null;

  // ── Theme ─────────────────────────────────────────────────────────────────
  const bg     = isDark ? '#0f0f0f' : '#ffffff';
  const bgSub  = isDark ? '#161616' : '#f8fafc';
  const bgCard = isDark ? '#1a1a1a' : '#f1f5f9';
  const border = isDark ? '#2a2a2a' : '#e2e8f0';
  const text   = isDark ? '#e2e8f0' : '#1e293b';
  const textMut= isDark ? '#64748b' : '#94a3b8';
  const acBg   = isDark ? '#1e3a5f' : '#dbeafe';
  const acText = isDark ? '#60a5fa' : '#1d4ed8';

  const accColor = dtMeta?.color || '#6366f1';

  return (
    <div data-testid="global-import-modal"
      style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'rgba(0,0,0,0.72)', backdropFilter:'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      <div style={{ width:'min(97vw,960px)', maxHeight:'92vh', display:'flex', flexDirection:'column', borderRadius:16, border:`1px solid ${border}`, background:bg, boxShadow:'0 28px 72px rgba(0,0,0,.65)', overflow:'hidden' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', alignItems:'center', padding:'14px 20px 12px', borderBottom:`1px solid ${border}`, background:bgSub, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, flex:1 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:`linear-gradient(135deg,${accColor},${accColor}aa)`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Upload size={15} color="white" />
            </div>
            <div>
              <div style={{ fontSize:15, fontWeight:700, color:text }}>
                {dtMeta ? `Import — ${dtMeta.label}` : 'AI / CSV Document Import'}
              </div>
              <div style={{ fontSize:11, color:textMut }}>Sarvam Vision OCR · CSV Bulk Import · ERP Auto-Save</div>
            </div>
          </div>
          <button data-testid="import-modal-close" onClick={onClose}
            style={{ padding:6, border:'none', borderRadius:6, background:'transparent', cursor:'pointer', color:textMut }}
            onMouseEnter={e => { if (!e.target) return; e.target.style.color = text; }}
            onMouseLeave={e => { if (!e.target) return; e.target.style.color = textMut; }}>
            <X size={18} />
          </button>
        </div>

        {/* ── Step bar ── */}
        <div style={{ display:'flex', alignItems:'center', padding:'10px 20px', borderBottom:`1px solid ${border}`, background:bgSub, flexShrink:0, gap:4 }}>
          {STEP_LABELS.map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <div style={{ width:22, height:22, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700,
                  background: i < step ? '#22c55e' : i === step ? acBg : isDark ? '#1f1f1f' : '#f1f5f9',
                  color: i < step ? '#fff' : i === step ? acText : textMut,
                  border: `1.5px solid ${i < step ? '#22c55e' : i === step ? acText : border}` }}>
                  {i < step ? <CheckCircle2 size={13} /> : i + 1}
                </div>
                {i === step && <span style={{ fontSize:11, fontWeight:600, color:acText }}>{label}</span>}
              </div>
              {i < STEP_LABELS.length - 1 && <div style={{ flex:1, height:1, background: i < step ? '#22c55e' : border, maxWidth:50 }} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Body ── */}
        <div style={{ flex:1, overflowY:'auto', padding:20 }}>

          {/* STEP 0: Select type */}
          {step === 0 && (
            <div data-testid="step-select-type">
              <p style={{ fontSize:12, color:textMut, marginBottom:16 }}>Choose what you want to import into the ERP:</p>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:10 }}>
                {DOC_TYPES.map(dt => (
                  <button key={dt.id} data-testid={`doctype-${dt.id}`}
                    onClick={() => { setDocType(dt.id); setStep(1); }}
                    style={{ padding:'14px 10px', borderRadius:10, border:`1.5px solid ${border}`, background:bgCard, cursor:'pointer', textAlign:'left', transition:'all .15s' }}
                    onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = dt.color; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; e.currentTarget.style.transform=''; }}>
                    <div style={{ width:28, height:28, borderRadius:7, background:`${dt.color}20`, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:8 }}>
                      {dt.csv ? <Table2 size={14} color={dt.color} /> : <Upload size={14} color={dt.color} />}
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:text, marginBottom:2 }}>{dt.label}</div>
                    <div style={{ fontSize:10, color:textMut, lineHeight:1.4 }}>{dt.desc}</div>
                    <div style={{ display:'flex', gap:4, marginTop:6 }}>
                      {dt.csv && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3, background:`${dt.color}15`, color:dt.color, fontWeight:700 }}>CSV</span>}
                      {dt.ai  && <span style={{ fontSize:9, padding:'1px 5px', borderRadius:3, background:'#8b5cf620', color:'#8b5cf6', fontWeight:700 }}>AI/PDF</span>}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 1: Choose method */}
          {step === 1 && dtMeta && (
            <div data-testid="step-choose-method">
              {preSelectedType === null && (
                <button onClick={() => { setStep(0); setDocType(null); }}
                  style={{ fontSize:11, color:textMut, background:'none', border:'none', cursor:'pointer', marginBottom:14, display:'flex', alignItems:'center', gap:4 }}>
                  ← Back to type selection
                </button>
              )}
              <div style={{ display:'flex', gap:14 }}>
                {dtMeta.csv && (
                  <button data-testid="method-csv"
                    onClick={() => { setMethod('csv'); setStep(2); }}
                    style={{ flex:1, padding:20, borderRadius:12, border:`2px solid ${border}`, background:bgCard, cursor:'pointer', textAlign:'left', transition:'all .15s' }}
                    onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = dtMeta.color; }}
                    onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; }}>
                    <Table2 size={26} color={dtMeta.color} />
                    <div style={{ fontSize:14, fontWeight:700, color:text, marginTop:8, marginBottom:4 }}>CSV / Spreadsheet Import</div>
                    <div style={{ fontSize:11, color:textMut, lineHeight:1.5 }}>
                      Paste CSV text or upload a .csv file. Fast bulk import with validation. Columns: <code style={{ color:dtMeta.color }}>{dtMeta.csvCols.join(', ')}</code>
                    </div>
                    <div style={{ marginTop:10, padding:'4px 10px', borderRadius:4, background:`${dtMeta.color}15`, display:'inline-block', fontSize:10, color:dtMeta.color, fontWeight:700 }}>Recommended for bulk data</div>
                  </button>
                )}
                {dtMeta.ai && (
                  <button data-testid="method-ai"
                    onClick={() => { setMethod('ai'); setStep(2); }}
                    style={{ flex:1, padding:20, borderRadius:12, border:`2px solid ${border}`, background:bgCard, cursor:'pointer', textAlign:'left', transition:'all .15s' }}
                    onMouseEnter={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = '#8b5cf6'; }}
                    onMouseLeave={e => { if (!e.currentTarget) return;  e.currentTarget.style.borderColor = border; }}>
                    <Upload size={26} color="#8b5cf6" />
                    <div style={{ fontSize:14, fontWeight:700, color:text, marginTop:8, marginBottom:4 }}>AI / PDF Import</div>
                    <div style={{ fontSize:11, color:textMut, lineHeight:1.5 }}>
                      Upload any PDF, bank statement, invoice, or scanned document. Sarvam Vision extracts financial data automatically.
                    </div>
                    <div style={{ marginTop:10, padding:'4px 10px', borderRadius:4, background:'#8b5cf615', display:'inline-block', fontSize:10, color:'#8b5cf6', fontWeight:700 }}>Powered by Sarvam Vision</div>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: Upload (CSV or PDF) */}
          {step === 2 && method === 'csv' && (
            <div data-testid="step-csv-upload">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:text }}>Paste CSV data or upload a .csv file</div>
                <button onClick={() => setStep(1)} style={{ fontSize:11, color:textMut, background:'none', border:'none', cursor:'pointer' }}>← Back</button>
              </div>
              {/* CSV column guide */}
              <div style={{ marginBottom:10, padding:'8px 12px', borderRadius:8, background:`${dtMeta?.color || '#6366f1'}10`, border:`1px solid ${dtMeta?.color || '#6366f1'}30`, fontSize:11 }}>
                <strong style={{ color: dtMeta?.color || '#6366f1' }}>Required columns:</strong>{' '}
                <code style={{ color:text }}>{dtMeta?.csvCols?.join(', ')}</code>
                <div style={{ fontSize:10, color:textMut, marginTop:3 }}>Row 1 = headers. Extra columns are ignored.</div>
              </div>
              {/* File upload shortcut */}
              <div style={{ display:'flex', gap:8, marginBottom:8 }}>
                <button onClick={() => csvRef.current?.click()}
                  style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${border}`, background:bgCard, color:text, cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', gap:5 }}>
                  <FileText size={12} /> Upload .csv file
                </button>
                <button data-testid="download-template-btn" onClick={handleDownloadTemplate}
                  style={{ padding:'5px 12px', borderRadius:6, border:`1px solid ${dtMeta?.color || '#6366f1'}60`, background:`${dtMeta?.color || '#6366f1'}10`, color:dtMeta?.color || '#6366f1', cursor:'pointer', fontSize:11, display:'flex', alignItems:'center', gap:5, fontWeight:600 }}>
                  <Download size={12} /> Download Template
                </button>
                <input ref={csvRef} type="file" accept=".csv,text/csv" style={{ display:'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { const r = new FileReader(); r.onload = ev => setCsvText(ev.target?.result || ''); r.readAsText(f); } }} />
              </div>
              <textarea
                data-testid="csv-input"
                value={csvText}
                onChange={e => setCsvText(e.target.value)}
                placeholder={`GLId,GLName,GlType,DrOpenBal,CrOpenBal\n160-AA0001,Cash Account,Assets,0,0\n...`}
                style={{ width:'100%', minHeight:200, padding:10, borderRadius:8, border:`1px solid ${border}`, background:bgCard, color:text, fontSize:11, fontFamily:'monospace', resize:'vertical', outline:'none', boxSizing:'border-box' }}
              />
              {csvText.trim() && (
                <button data-testid="csv-parse-btn" onClick={handleCsvParse}
                  style={{ marginTop:10, width:'100%', padding:10, borderRadius:8, background:`${dtMeta?.color || '#6366f1'}`, color:'#fff', border:'none', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                  Preview {parseCSVText(csvText).length} rows →
                </button>
              )}
            </div>
          )}

          {step === 2 && method === 'ai' && (
            <div data-testid="step-pdf-upload">
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:text }}>Upload PDF for AI extraction</div>
                <button onClick={() => setStep(1)} style={{ fontSize:11, color:textMut, background:'none', border:'none', cursor:'pointer' }}>← Back</button>
              </div>
              <div data-testid="pdf-dropzone"
                onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)} onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                style={{ border:`2px dashed ${isDragging ? '#8b5cf6' : border}`, borderRadius:12, padding:'44px 20px', textAlign:'center', cursor:'pointer', background: isDragging ? '#8b5cf610' : bgCard, transition:'all .15s' }}>
                <input ref={fileRef} type="file" accept="application/pdf" data-testid="pdf-file-input"
                  onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f); }} style={{ display:'none' }} />
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:10 }}>
                  {file ? <FileText size={28} color="#8b5cf6" /> : <Upload size={28} color={textMut} />}
                  {file
                    ? <><div style={{ fontSize:13, fontWeight:700, color:'#22c55e' }}>{file.name}</div><div style={{ fontSize:11, color:textMut }}>{(file.size/1024).toFixed(0)} KB · Click to change</div></>
                    : <><div style={{ fontSize:14, fontWeight:600, color:text }}>Drop PDF here or click to browse</div><div style={{ fontSize:11, color:textMut }}>Max 10 pages · Up to 20 MB · PDF only</div></>
                  }
                </div>
              </div>
              {file && (
                <button data-testid="parse-btn" onClick={handleParse}
                  style={{ marginTop:14, width:'100%', padding:10, borderRadius:8, background:'linear-gradient(135deg,#8b5cf6,#6366f1)', color:'#fff', border:'none', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                  Analyze with Sarvam Vision
                </button>
              )}
              {/* AI parsing progress — step-by-step checklist */}
              {parsing && (
                <div data-testid="parse-progress" style={{ marginTop:16, padding:'12px 14px', borderRadius:10, border:`1px solid ${border}`, background:bgCard }}>
                  {PARSE_STAGES.map((label, i) => (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:9, padding:'5px 0', opacity: i <= parseStage ? 1 : 0.4 }}>
                      {i < parseStage
                        ? <CheckCircle2 size={14} color="#22c55e" />
                        : i === parseStage
                          ? <Loader2 size={14} style={{ animation:'spin 1s linear infinite', color:'#8b5cf6' }} />
                          : <div style={{ width:14, height:14, borderRadius:'50%', border:`1.5px solid ${border}` }} />}
                      <span style={{ fontSize:12, color: i <= parseStage ? text : textMut, fontWeight: i === parseStage ? 700 : 400 }}>{label}</span>
                    </div>
                  ))}
                </div>
              )}
              {parseError && (
                <div style={{ marginTop:12, padding:'8px 12px', borderRadius:8, background:'#ef444420', border:'1px solid #ef4444', color:'#ef4444', fontSize:12, display:'flex', alignItems:'center', gap:8 }}>
                  <AlertCircle size={14} /> {parseError}
                  <button onClick={() => setParseError('')} style={{ marginLeft:'auto', background:'none', border:'none', color:'#ef4444', cursor:'pointer', fontSize:11 }}>Dismiss</button>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Review & Map */}
          {step === 3 && method === 'csv' && (
            <div data-testid="step-csv-review">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:text }}>Preview CSV Rows</div>
                  <div style={{ fontSize:11, color:textMut }}>{csvRows.length} rows · Review before saving</div>
                </div>
                <button onClick={() => setStep(2)} style={{ fontSize:11, color:textMut, background:'none', border:'none', cursor:'pointer' }}>← Edit CSV</button>
              </div>
              <div style={{ border:`1px solid ${border}`, borderRadius:10, overflow:'hidden', maxHeight:'45vh', overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                  <thead>
                    <tr style={{ background:bgSub }}>
                      {dtMeta?.csvCols.map(col => (
                        <th key={col} style={{ padding:'7px 10px', textAlign:'left', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}`, whiteSpace:'nowrap' }}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.slice(0, 200).map((row, idx) => (
                      <tr key={idx} style={{ borderBottom:`1px solid ${border}`, background: idx%2===0 ? 'transparent' : isDark ? '#ffffff06' : '#f8fafc' }}>
                        {dtMeta?.csvCols.map(col => (
                          <td key={col} style={{ padding:'6px 10px', color:text, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row[col] || '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {saveError && <div style={{ marginTop:10, padding:'8px 12px', borderRadius:8, background:'#ef444420', border:'1px solid #ef4444', color:'#ef4444', fontSize:12 }}>{saveError}</div>}
            </div>
          )}

          {step === 3 && method === 'ai' && (
            <div data-testid="step-ai-review">
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:text }}>Map GL Accounts to Extracted Rows</div>
                  <div style={{ fontSize:11, color:textMut }}>{rows.length} rows extracted · Assign a GL account to each</div>
                </div>
                <div style={{ position:'relative' }}>
                  <Search size={12} style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:textMut }} />
                  <input placeholder="Search accounts..." value=""
                    onChange={e => loadAccounts(e.target.value)}
                    style={{ paddingLeft:24, paddingRight:8, paddingTop:5, paddingBottom:5, fontSize:11, borderRadius:6, border:`1px solid ${border}`, background:bgCard, color:text, outline:'none', width:150 }} />
                </div>
              </div>
              {matchStats && (
                <div data-testid="match-stats-banner" style={{ display:'flex', gap:14, alignItems:'center', marginBottom:12, padding:'8px 12px', borderRadius:8, background: matchStats.matched > 0 ? '#22c55e15' : '#f59e0b15', border:`1px solid ${matchStats.matched > 0 ? '#22c55e' : '#f59e0b'}40` }}>
                  <CheckCircle2 size={14} color="#22c55e" />
                  <span style={{ fontSize:12, color:text }}>
                    <strong style={{ color:'#22c55e' }}>{matchStats.matched}</strong> of {matchStats.total} accounts auto-matched by Account Id ·{' '}
                    <strong style={{ color:'#f59e0b' }}>{matchStats.unmatched}</strong> need manual mapping below (unmatched rows shown first)
                  </span>
                </div>
              )}
              <input data-testid="import-narration" placeholder="Narration / Reference..." value={narration}
                onChange={e => setNarration(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', borderRadius:8, border:`1px solid ${border}`, background:bgCard, color:text, fontSize:12, marginBottom:12, boxSizing:'border-box', outline:'none' }} />
              {rows.length === 0
                ? <div style={{ textAlign:'center', padding:32, color:textMut, fontSize:12 }}>No financial rows could be extracted. Upload a different document or try CSV import.</div>
                : (
                  <div style={{ border:`1px solid ${border}`, borderRadius:10, overflow:'hidden' }}>
                    <div style={{ maxHeight:'42vh', overflowY:'auto', overflowX:'auto' }}>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11 }}>
                        <thead>
                          <tr style={{ background:bgSub }}>
                            {docType === 'opening_balance' && <th style={{ padding:'7px 10px', textAlign:'left', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}` }}>Account Id (PDF)</th>}
                            <th style={{ padding:'7px 10px', textAlign:'left', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}` }}>Description</th>
                            <th style={{ padding:'7px 10px', textAlign:'right', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}` }}>Amount</th>
                            <th style={{ padding:'7px 10px', textAlign:'center', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}` }}>Dr/Cr</th>
                            <th style={{ padding:'7px 10px', textAlign:'left', color:textMut, fontWeight:600, borderBottom:`1px solid ${border}`, minWidth:180 }}>GL Account</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom:`1px solid ${border}`, background: idx%2===0 ? 'transparent' : isDark ? '#ffffff06' : '#f8fafc' }}>
                              {docType === 'opening_balance' && (
                                <td style={{ padding:'6px 10px', fontFamily:'monospace', fontSize:10 }}>
                                  <span style={{ color: row.matched ? '#22c55e' : '#f59e0b', fontWeight:700 }}>{row.matched ? '●' : '○'}</span> {row.accountId}
                                </td>
                              )}
                              <td style={{ padding:'6px 10px', color:text, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.description}</td>
                              <td style={{ padding:'6px 10px', textAlign:'right', color: row.type==='DR'?'#f87171':'#4ade80', fontFamily:'monospace', fontWeight:600 }}>
                                {row.amount?.toLocaleString('en-IN', { minimumFractionDigits:2 })}
                              </td>
                              <td style={{ padding:'6px 10px', textAlign:'center' }}>
                                <select value={row.type} onChange={e => { const r=[...rows]; r[idx]={...r[idx],type:e.target.value}; setRows(r); }}
                                  style={{ fontSize:10, padding:'2px 4px', borderRadius:4, border:`1px solid ${border}`, background:bgCard, color:row.type==='DR'?'#f87171':'#4ade80', cursor:'pointer' }}>
                                  <option value="DR">DR</option><option value="CR">CR</option><option value="UNKNOWN">?</option>
                                </select>
                              </td>
                              <td style={{ padding:'6px 10px' }}>
                                <select data-testid={`gl-select-${idx}`} value={row.glId}
                                  onChange={e => { const acct=accounts.find(a=>a.GLId===e.target.value); const r=[...rows]; r[idx]={...r[idx],glId:e.target.value,glName:acct?.GLName||r[idx].glName}; setRows(r); }}
                                  style={{ width:'100%', fontSize:11, padding:'4px 6px', borderRadius:6, border:`1px solid ${row.glId ? '#22c55e' : border}`, background:bgCard, color: row.glId ? text : textMut, cursor:'pointer' }}>
                                  <option value="">— select account —</option>
                                  {row.glId && !accounts.some(a => a.GLId === row.glId) && (
                                    <option value={row.glId}>{row.glId} · {row.glName}</option>
                                  )}
                                  {acctLoading && <option disabled>Loading...</option>}
                                  {accounts.map(a => <option key={a.GLId} value={a.GLId}>{a.GLId} · {a.GLName}</option>)}
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              {saveError && <div style={{ marginTop:10, padding:'8px 12px', borderRadius:8, background:'#ef444420', border:'1px solid #ef4444', color:'#ef4444', fontSize:12 }}>{saveError}</div>}
            </div>
          )}

          {/* STEP 4: Done */}
          {step === 4 && (
            <div data-testid="step-saved" style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:220, gap:14 }}>
              <div style={{ width:64, height:64, borderRadius:'50%', background:'#22c55e20', border:'2px solid #22c55e', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <CheckCircle2 size={32} color="#22c55e" />
              </div>
              <div style={{ fontSize:16, fontWeight:700, color:'#22c55e' }}>Import Complete!</div>
              {saveResult && (
                <div style={{ fontSize:12, color:textMut, textAlign:'center', lineHeight:1.8 }}>
                  {saveResult.saved !== undefined && <div>{saveResult.saved} AI-mapped entries saved</div>}
                  {saveResult.inserted !== undefined && <div>{saveResult.inserted} rows inserted · {saveResult.updated} updated</div>}
                  {saveResult.errors?.length > 0 && <div style={{ color:'#f87171', marginTop:6 }}>{saveResult.errors.length} row(s) skipped — {saveResult.errors[0]}</div>}
                </div>
              )}
              <button onClick={onClose} style={{ marginTop:8, padding:'9px 24px', borderRadius:8, background:'#22c55e', color:'#fff', border:'none', cursor:'pointer', fontSize:13, fontWeight:700 }}>Close</button>
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        {(step === 3) && (
          <div style={{ padding:'12px 20px', borderTop:`1px solid ${border}`, background:bgSub, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <span style={{ fontSize:11, color:textMut }}>
              {method === 'csv' ? `${csvRows.length} rows ready` : `${rows.filter(r=>r.glId).length} of ${rows.length} rows mapped`}
            </span>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setStep(2)} style={{ padding:'7px 14px', borderRadius:8, background:'transparent', border:`1px solid ${border}`, color:text, cursor:'pointer', fontSize:12 }}>← Back</button>
              <button data-testid="save-import-btn"
                onClick={method === 'csv' ? handleSaveCSV : handleSaveAI}
                disabled={saving}
                style={{ padding:'7px 20px', borderRadius:8, background: saving ? '#374151' : 'linear-gradient(135deg,#22c55e,#16a34a)', color:'#fff', border:'none', cursor: saving ? 'not-allowed' : 'pointer', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', gap:6 }}>
                {saving ? <Loader2 size={13} style={{ animation:'spin 1s linear infinite' }} /> : <Save size={13} />}
                {saving ? 'Saving...' : `Save to ERP`}
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`@keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}
